/**
 * Data and handler wiring for the Production Line detail page
 * (production-lines): route params, session, the line read, the intake
 * journey behind it, the line's videos and runs, the live-work refresh
 * timer, the derived head values, and every server action the page can
 * take. Moved verbatim out of ProductionLineDetailPage.tsx, same hook order.
 */

import {
  useCallback,
  useEffect,
  useId,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from 'react';
import { useNavigate, useParams, type NavigateFunction } from 'react-router-dom';

import { useLanguage } from '../../i18n/language.js';
import { newIdempotencyKey } from '../../workspace/api.js';
import { useSession } from '../../workspace/session.js';
import {
  changeProductionLineLifecycle,
  confirmMerchantCreativeCard,
  createMerchantCreativeRun,
  createProductionLineCreativeRun,
  fetchProductionLine,
  regenerateMerchantCreativeCard,
  type MerchantCreativeRunView,
  type MerchantProfileView,
  type ProductionLineReadView,
  type RegenerationReason,
} from '../../merchant/api.js';
import { workspaceCopy, type WorkspaceCopy } from '../../merchant/copy.js';
import {
  useIntakeJourney,
  useOnline,
  type IntakeJourney,
  type WorkspaceSnapshot,
} from '../../merchant/data.js';
import {
  domainOf,
  runIsWorking,
  type LineSummary,
  type VideoItem,
} from '../../merchant/model.js';
import {
  useMerchantWorkspaceIdentity,
  useWorkspace,
} from '../../merchant/shell-context.js';
import { useToast } from '../../merchant/ui/primitives.js';
import {
  TABS,
  journeyOf,
  type ConfiguredLine,
  type Journey,
  type LineModal,
  type LineTab,
} from './productionLineJourney.js';

export interface ProductionLineDetailModel {
  readonly language: 'en' | 'ar';
  readonly copy: WorkspaceCopy;
  readonly navigate: NavigateFunction;
  readonly toast: ReturnType<typeof useToast>;
  readonly workspace: WorkspaceSnapshot;
  readonly productionLineId: string;
  readonly tab: LineTab;
  readonly titleId: string;
  readonly online: boolean;
  readonly detailFailed: boolean;
  readonly moreOpen: boolean;
  readonly setMoreOpen: Dispatch<SetStateAction<boolean>>;
  readonly modal: LineModal;
  readonly setModal: Dispatch<SetStateAction<LineModal>>;
  readonly busy: string | null;
  readonly actionError: string | null;
  readonly selectedDirection: string | null;
  readonly setSelectedDirection: Dispatch<SetStateAction<string | null>>;
  readonly askingAngle: boolean;
  readonly setAskingAngle: Dispatch<SetStateAction<boolean>>;
  readonly setAnotherVersion: Dispatch<SetStateAction<boolean>>;
  readonly summary: LineSummary | null;
  readonly realLine: ConfiguredLine | null;
  readonly journeyState: IntakeJourney;
  readonly view: MerchantProfileView | null;
  readonly workspaceName: string;
  readonly lineVideos: readonly VideoItem[];
  readonly latestRun: MerchantCreativeRunView | null;
  readonly journey: Journey;
  readonly status: LineSummary['status'];
  readonly domain: string;
  readonly prompt: string;
  readonly title: string;
  readonly updatedAt: string | null;
  readonly startVideo: () => void;
  readonly makeIt: () => void;
  readonly askAngle: (reason: RegenerationReason | null) => void;
  readonly makeAnotherOnLine: () => void;
  readonly lifecycle: (action: 'pause' | 'resume' | 'archive') => void;
}

/**
 * Every step is read from the intake behind the line. Nothing advances on a
 * timer; the page only asks the server again while it says work is live.
 */
export function useProductionLineDetail(): ProductionLineDetailModel {
  const { language } = useLanguage();
  const copy = workspaceCopy(language);
  const { session } = useSession();
  const navigate = useNavigate();
  const toast = useToast();
  const workspace = useWorkspace();
  const { businessName, setBusinessName, setProfileConfirmed } =
    useMerchantWorkspaceIdentity();
  const { productionLineId = '', tab: routeTab } = useParams<{
    productionLineId: string;
    tab?: string;
  }>();
  const titleId = useId();
  const online = useOnline();
  const merchant =
    session.status === 'authenticated' && session.account.role === 'MERCHANT';
  const tab: LineTab = TABS.includes(routeTab as LineTab)
    ? (routeTab as LineTab)
    : 'overview';

  const [detail, setDetail] = useState<ProductionLineReadView | null>(null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [moreOpen, setMoreOpen] = useState(false);
  const [modal, setModal] = useState<LineModal>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [selectedDirection, setSelectedDirection] = useState<string | null>(null);
  const [askingAngle, setAskingAngle] = useState(false);
  const [anotherVersion, setAnotherVersion] = useState(false);

  const loadDetail = useCallback(async () => {
    if (productionLineId.length === 0) return;
    setDetailFailed(false);
    try {
      setDetail(await fetchProductionLine(productionLineId));
    } catch {
      setDetailFailed(true);
    }
  }, [productionLineId]);

  useEffect(() => {
    void loadDetail();
  }, [loadDetail]);

  const summary = workspace.lines.find((line) => line.id === productionLineId) ?? null;
  const realLine = detail !== null && !('pending' in detail) ? detail : null;
  const intakeId =
    summary?.intakeId ??
    (detail !== null && 'pending' in detail ? detail.intakeId : null) ??
    realLine?.brief?.intakeId ??
    null;
  const journeyState = useIntakeJourney(intakeId, {
    merchant,
    onChange: (next: MerchantProfileView) => {
      setBusinessName(next.business.displayName);
      setProfileConfirmed(next.profile?.reviewState === 'APPROVED');
    },
  });
  const view = journeyState.view;
  const workspaceName =
    businessName?.trim() || view?.business.displayName || copy.nav.yourBusiness;

  const lineVideos = useMemo(
    () => workspace.videos.filter((video) => video.lineId === productionLineId),
    [productionLineId, workspace.videos],
  );
  const runs: readonly MerchantCreativeRunView[] = useMemo(() => {
    const own = lineVideos.map((video) => video.run);
    if (own.length > 0) return own;
    return view?.creativeRuns ?? [];
  }, [lineVideos, view?.creativeRuns]);
  const latestRun = runs[0] ?? null;

  // Live work is server work: while a run is being made, re-read the
  // workspace so the card and the finished video appear without a reload.
  const working = runs.some(runIsWorking);
  useEffect(() => {
    if (!working) return undefined;
    const timer = window.setInterval(() => void workspace.refresh(), 5000);
    return () => {
      window.clearInterval(timer);
    };
  }, [working, workspace]);

  const journey: Journey = journeyOf(view, detailFailed, latestRun, anotherVersion);

  const status =
    summary?.status ??
    (journey === 'analysis'
      ? 'analyzing'
      : journey === 'profile'
        ? 'needs-input'
        : 'draft');
  const domain = domainOf(
    summary?.websiteUrl ??
      view?.business.sources.find((s) => s.platform === 'WEBSITE')?.canonicalUrl ??
      null,
  );
  const prompt = summary?.prompt || view?.contextNote || '';
  const title =
    summary?.title ??
    view?.business.displayName ??
    (detail !== null && 'pending' in detail
      ? detail.title
      : (realLine?.productionLine.title ?? ''));
  const updatedAt = summary?.updatedAt ?? view?.analysis.progress.lastUpdatedAt ?? null;

  const act = async (key: string, effect: () => Promise<unknown>) => {
    setBusy(key);
    setActionError(null);
    try {
      await effect();
      await Promise.all([workspace.refresh(), journeyState.load()]);
    } catch {
      setActionError(copy.line.actionFailed);
    } finally {
      setBusy(null);
    }
  };

  const startVideo = () => {
    const handoff = view?.result?.handoffArtifactId;
    if (
      intakeId === null ||
      selectedDirection === null ||
      handoff === null ||
      handoff === undefined
    )
      return;
    setAnotherVersion(false);
    void act('start', () =>
      createMerchantCreativeRun(
        intakeId,
        { directionArtifactId: selectedDirection, handoffArtifactId: handoff },
        newIdempotencyKey(),
      ),
    );
  };

  const makeIt = () => {
    if (intakeId === null || latestRun?.card === null || latestRun === null) return;
    const cardArtifactId = latestRun.card.cardArtifactId;
    void act('make', () =>
      confirmMerchantCreativeCard(
        intakeId,
        latestRun.creativeRunId,
        cardArtifactId,
        newIdempotencyKey(),
      ),
    );
  };

  const askAngle = (reason: RegenerationReason | null) => {
    if (intakeId === null || latestRun === null) return;
    setAskingAngle(false);
    void act('angle', () =>
      regenerateMerchantCreativeCard(
        intakeId,
        latestRun.creativeRunId,
        reason,
        newIdempotencyKey(),
      ),
    );
  };

  const makeAnotherOnLine = () => {
    if (realLine === null) return;
    void act('another', () =>
      createProductionLineCreativeRun(
        realLine.productionLine.productionLineId,
        newIdempotencyKey(),
      ),
    );
  };

  const lifecycle = (action: 'pause' | 'resume' | 'archive') => {
    if (realLine === null) return;
    setModal(null);
    void act(action, async () => {
      await changeProductionLineLifecycle(
        realLine.productionLine.productionLineId,
        action,
        newIdempotencyKey(),
      );
      await loadDetail();
      toast(
        action === 'archive'
          ? copy.line.activity.archived
          : action === 'pause'
            ? copy.line.activity.paused
            : copy.line.resume,
      );
    });
  };

  return {
    language,
    copy,
    navigate,
    toast,
    workspace,
    productionLineId,
    tab,
    titleId,
    online,
    detailFailed,
    moreOpen,
    setMoreOpen,
    modal,
    setModal,
    busy,
    actionError,
    selectedDirection,
    setSelectedDirection,
    askingAngle,
    setAskingAngle,
    setAnotherVersion,
    summary,
    realLine,
    journeyState,
    view,
    workspaceName,
    lineVideos,
    latestRun,
    journey,
    status,
    domain,
    prompt,
    title,
    updatedAt,
    startVideo,
    makeIt,
    askAngle,
    makeAnotherOnLine,
    lifecycle,
  };
}
